import { ProcessedCourse } from "@/common/course/types";
import isCourseArchived from "@/lib/course/utils/isCourseArchived";
import useUserEnrollments from "@/components/CourseContent/CourseContext/hooks/useUserEnrollments";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import {
  maxWidthContainer,
  fullWidthContainer,
} from "@/components/CourseContent/styles/constants";
import useWebview from "@/webview/useWebview";
import useEmailOptIn from "@/components/CourseContent/CourseContext/hooks/useEmailOptIn";
import { toggleEmailOptIn as trackToggleEmailOptIn } from "@/lib/segment";
import parse from "html-react-parser";
import { PartialPartner } from "@/common/partner/types";
import Link from "next/link";
import Image from "next/image";
import DynamicBadges from "@/components/ProgramDetailLayout/templates/components/TaggedHeader/DynamicBadges";
import EmailOptIn from "@/components/EmailOptIn/EmailOptIn";
import { hideEmailOptInForCourse } from "@/components/CourseContent/CourseRunSelector/constants";
import CourseRunSelector from "@/components/CourseContent/ContentRefresh/CourseRunSelector";
import ExpandableTextByLines from "@/components/CourseContent/Expand/ExpandableTextByLines";
import { useSingleCourseTrafficConfig } from "@/monarch/rules/singleCourseTraffic/useSingleCourseTraffic";
import VerifiedBadge from "@/components/VerifiedBadge";
import useLeadWithPaid from "@/monarch/rules/leadWithPaid/useLeadWithPaid";
import FactoidBar from "@/components/CourseContent/ContentRefresh/FactoidBar";
import { getPartnersDisplayNames } from "@/lib/partner/utils/getPartnerDisplayName";
import { interpolateTemplateString } from "@/utils/functions/interpolateTemplateString";
import Ribbon from "@/components/Ribbon";
import CurrencyInlinePill from "@/components/CurrencySelector/CurrencyInlinePill";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import formatProgramPrice from "@/lib/checkout-geo-pricing/formatProgramPrice";
import type { DetailPricingData } from "@/lib/checkout-geo-pricing";
import HeroAccentCallout from "@/components/GeoPricing/HeroAccentCallout";
import useTranslations from "@/i18n/useTranslations";

export interface ICoursePageHeroProps {
  course: ProcessedCourse;
  /** Geo-adjusted display price from the parent template. */
  displayPrice: number | null;
  /** ISO currency code for displayPrice formatting. */
  currency?: string;
  pricingData?: DetailPricingData | null;
  variant?: "light" | "dark";
  priceLabel?: string;
  pricePosition?: "belowTitle" | "aboveCta";
  simpleMobileHeader?: boolean;
  paymentOption: "free" | "upgradable" | "paid" | null;
  showDynamicBadges?: boolean;
}

const VARIANT_STYLES = {
  light: {
    bg: "bg-putty-100",
    text: "text-[#002626]",
    badge: "bg-putty-400 border-putty-700",
    badgeText: "text-black",
    border: "border-putty-400",
  },
  dark: {
    bg: "bg-primary-500",
    text: "text-white",
    badge: "bg-[#BEF4E5] border-[#004f4f]",
    badgeText: "text-primary-500",
    border: "border-[#003838]",
  },
};

const LOGO_SIZE_MAP = {
  sm: { width: 120, height: 60 },
  md: { width: 180, height: 90 },
  lg: { width: 240, height: 120 },
} as const;

export default function CourseTaggedHeader({
  course,
  displayPrice,
  currency = "USD",
  pricingData,
  variant = "light",
  priceLabel,
  pricePosition = "belowTitle",
  simpleMobileHeader = false,
  paymentOption,
  showDynamicBadges = true,
}: ICoursePageHeroProps) {
  const t = useTranslations("Course");
  const {
    displayExperience,
    verifiedBadge,
    partnerLogo,
    showHeaderRibbon,
    headerRibbonText,
    headerBackgroundImageUrl,
    showHeaderFactoidBar,
    isLoading,
    factoidsBar,
    insertBadges,
    currencySelector,
    showHeaderPrice,
    showHeaderPriceMobile,
    straightToCheckout,
  } = useSingleCourseTrafficConfig({ course });
  const { emailOptIn, setEmailOptIn } = useEmailOptIn();
  const { isUserEnrolled } = useUserEnrollments();
  const leadWithPaidData = useLeadWithPaid(course);
  const { getUrl } = useWebview();

  const { activeCourseRun } = course;
  const partners = course.partners.length ? course.partners : [];
  const isArchived = activeCourseRun ? isCourseArchived(activeCourseRun) : true;
  const id = course.productUuid;
  const productName = course.productName;
  const titlePartner = getPartnersDisplayNames(course.partners);
  const title = `${titlePartner}: ${productName}`;
  const courseDescription = parse(course.productShortDescription);

  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.light;
  const breadcrumbVariant = variant === "light" ? "dark" : "light";
  const backgroundStyle = headerBackgroundImageUrl
    ? {
        background: `linear-gradient(0deg, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0.65) 100%), url(${headerBackgroundImageUrl}) lightgray 50% / cover no-repeat`,
      }
    : undefined;

  const resolvedPriceLabel = priceLabel ?? t("earnACertificateFor");
  const formattedPrice =
    displayPrice != null
      ? `${resolvedPriceLabel} ${formatProgramPrice(displayPrice, currency)}`
      : null;
  const priceClassName = "text-white mt-6 mb-0 text-lg md:text-xl";
  const isLegacyPriceVariant =
    displayExperience === "valueOrientedUser" ||
    displayExperience === "researcher";
  const baseShowPrice = isLegacyPriceVariant && !currencySelector;
  const showPriceMobile =
    (baseShowPrice || showHeaderPriceMobile === true) &&
    showHeaderPriceMobile !== false;
  const showPriceDesktop =
    (baseShowPrice || showHeaderPrice === true) && showHeaderPrice !== false;

  const logoSize = LOGO_SIZE_MAP[partnerLogo?.size ?? "sm"];
  const logoElements = partners.map((partner: PartialPartner) => {
    const imageClassName = "max-w-full bg-white p-2 rounded-[8px] shadow-lg";
    return partner.showOnSchoolsAndPartnersPage ? (
      <Link
        className="d-inline-block rounded-[8px]"
        key={partner.key}
        href={getUrl(`/school/${partner.slug}`) || ""}
      >
        <Image
          src={partner.logoUrl || ""}
          width={logoSize.width}
          height={logoSize.height}
          alt={title}
          title={title}
          key={partner.key}
          className={imageClassName}
        />
      </Link>
    ) : (
      <Image
        src={partner.logoUrl || ""}
        width={logoSize.width}
        height={logoSize.height}
        alt={title}
        title={title}
        key={partner.key}
        className={imageClassName}
      />
    );
  });

  const showBadge = ["valueOrientedUser", "researcher", "referral"].includes(
    displayExperience ?? "",
  );

  const verifiedBadgeEl = showBadge ? (
    <VerifiedBadge
      partners={partners}
      className={`${styles.badge} ${styles.badgeText}`}
      textClassName={styles.badgeText}
      verifiedBadgeConfig={verifiedBadge}
    />
  ) : null;

  const showSimpleMobileHeader =
    displayExperience === "valueOrientedUser" && simpleMobileHeader;

  const resolvedRibbonText = parse(
    interpolateTemplateString(headerRibbonText, {
      "{{partnerName}}": titlePartner,
      "{{courseName}}": productName,
    }),
  );

  const toggleEmailOptIn = () => {
    const optIn = !emailOptIn;
    setEmailOptIn(optIn);
    if (activeCourseRun?.key) {
      trackToggleEmailOptIn({ courseRunKey: activeCourseRun.key, optIn });
    }
  };

  return (
    <>
      <div
        className={`${styles.bg} ${styles.text} py-8 md:py-12 w-full`}
        style={backgroundStyle}
      >
        <div className={`${maxWidthContainer} ${fullWidthContainer} pt-4`}>
          <Breadcrumbs
            path={course.productUrlSlug || ""}
            pageName={title}
            linksToShowOnSmallScreen={1}
            containerClassName="not-prose"
            variant={breadcrumbVariant}
          />
        </div>

        <div className={`${maxWidthContainer} ${fullWidthContainer} z-10`}>
          <div className="pb-6 pt-0 px-0 md:flex md:items-start md:justify-between transition-colors duration-300">
            <div className="flex flex-col md:flex-row gap-5 md:gap-10 items-start w-full">
              {/* Content column */}
              <div className="flex flex-col gap-5 flex-grow min-w-0">
                <div className="flex flex-col">
                  {!partnerLogo && (
                    <div className="flex flex-col md:flex-row md:justify-start items-start md:items-center gap-2.5 py-4">
                      <div className="flex md:justify-start items-center gap-2.5 w-full md:w-auto">
                        {logoElements}
                      </div>
                      {showBadge && (
                        <div className="flex flex-row gap-2 w-fill md:w-auto justify-start">
                          {verifiedBadgeEl}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Logo — above title position */}
                  {partnerLogo?.position === "aboveTitle" && (
                    <div className="flex justify-center md:justify-start items-center gap-2.5 py-4">
                      {logoElements}
                    </div>
                  )}

                  {/* Badge — above-CTA logo position: badge floats above h1 instead */}
                  {partnerLogo?.position === "aboveCta" && showBadge && (
                    <div className="flex flex-row gap-2 justify-start py-4">
                      {verifiedBadgeEl}
                    </div>
                  )}

                  <h1
                    className={`m-0 p-0 tracking-tight font-black ${styles.text}`}
                  >
                    {title}
                  </h1>

                  {pricePosition === "belowTitle" && (
                    <>
                      {showPriceMobile && (
                        <p className={`${priceClassName} block md:hidden`}>
                          {formattedPrice}
                        </p>
                      )}
                      {showPriceDesktop && (
                        <p className={`${priceClassName} hidden md:block`}>
                          {formattedPrice}
                        </p>
                      )}
                    </>
                  )}
                  {pricePosition === "aboveCta" && showPriceMobile && (
                    <p className={`${priceClassName} block md:hidden`}>
                      {formattedPrice}
                    </p>
                  )}

                  <div
                    className={!showSimpleMobileHeader ? "" : "hidden md:block"}
                  >
                    {insertBadges && (
                      <DynamicBadges
                        courseData={course}
                        styles={{
                          badge: styles.badge,
                          badgeText: styles.badgeText,
                        }}
                        insertBadges={insertBadges}
                      />
                    )}
                    {!showHeaderFactoidBar && showDynamicBadges && (
                      <DynamicBadges
                        courseData={course}
                        styles={{
                          badge: styles.badge,
                          badgeText: styles.badgeText,
                        }}
                        leadWithPaidData={leadWithPaidData}
                      >
                        {partnerLogo?.position === "aboveTitle" &&
                          verifiedBadgeEl}
                      </DynamicBadges>
                    )}
                    <ExpandableTextByLines
                      text={courseDescription}
                      className={`md:[&_p]:text-base [&_p]:m-0 [&_p]:${styles.text} w-full mt-6`}
                      maxLines={2}
                    />

                    <HeroAccentCallout
                      pricingData={pricingData}
                      variant={variant}
                      className="mt-4"
                      location="course-detail-page"
                    />

                    {currencySelector?.placement === "inline-dropdown" &&
                      displayPrice && (
                        <FeatureFlagComponentResolverClient
                          flagKey="NEXT_PUBLIC_FEATURE_MULTI_CURRENCY_ACCEPTANCE"
                          variants={{
                            legacy: null,
                            test: (
                              <CurrencyInlinePill
                                price={displayPrice}
                                label={currencySelector?.label}
                                mobileLabel={currencySelector?.mobileLabel}
                                location="single-course-detail-page"
                                tooltip={currencySelector?.tooltip}
                                countryAutoDetection={
                                  currencySelector?.countryAutoDetection
                                }
                                variant={
                                  variant === "dark" ? "inverse" : "default"
                                }
                                className="mt-2"
                              />
                            ),
                          }}
                          fallback="legacy"
                        />
                      )}
                  </div>
                </div>
              </div>
              <div className="flex w-full md:w-auto self-center justify-center">
                <div className="flex w-full md:w-auto md:min-w-[300px]">
                  <div className="flex gap-4 flex-col w-full text-primary-500">
                    {showPriceDesktop && pricePosition === "aboveCta" && (
                      <p
                        className={`${priceClassName} text-center hidden md:block`}
                      >
                        {formattedPrice}
                      </p>
                    )}
                    {partnerLogo?.position === "aboveCta" && (
                      <div className="flex justify-center items-center mb-[-64px] relative z-10">
                        {logoElements}
                      </div>
                    )}
                    <CourseRunSelector
                      course={course}
                      name="hero-cta"
                      location="hero"
                      pricingData={pricingData}
                      className={
                        partnerLogo?.position === "aboveCta"
                          ? "pt-16 md:pt-16"
                          : ""
                      }
                      auditTextClassName={styles.text}
                      straightToCheckout={straightToCheckout}
                    />
                  </div>
                </div>
              </div>

              {showSimpleMobileHeader && !showHeaderFactoidBar && (
                <div className="block md:hidden">
                  <DynamicBadges
                    courseData={course}
                    styles={{
                      badge: styles.badge,
                      badgeText: styles.badgeText,
                    }}
                    leadWithPaidData={leadWithPaidData}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            {showHeaderFactoidBar && (
              <div className="w-full block md:hidden">
                <FactoidBar
                  courseData={course}
                  paymentOption={paymentOption}
                  leadWithPaidData={leadWithPaidData}
                  isFactoidsBarFieldsLoading={isLoading}
                  monarchFactoidsBar={factoidsBar}
                  isHeaderFactoidBar={true}
                />
              </div>
            )}
            {!hideEmailOptInForCourse.includes(id) &&
              !isUserEnrolled &&
              !isArchived &&
              activeCourseRun?.isEnrollable && (
                <EmailOptIn
                  optIn={emailOptIn}
                  partners={partners}
                  title={productName}
                  toggleEmailOptIn={toggleEmailOptIn}
                  variant="secondary"
                  labelClassName={`${styles.text} text-xs md:text-sm`}
                />
              )}
            {showHeaderFactoidBar && (
              <div className="w-full hidden md:block">
                <FactoidBar
                  courseData={course}
                  paymentOption={paymentOption}
                  leadWithPaidData={leadWithPaidData}
                  isFactoidsBarFieldsLoading={isLoading}
                  monarchFactoidsBar={factoidsBar}
                  isHeaderFactoidBar={true}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {showHeaderRibbon && <Ribbon>{resolvedRibbonText}</Ribbon>}
    </>
  );
}
